import { useCallback, useMemo } from "react";

import { useLensIdSearchParameter } from "../../lens-insights/hooks/useLensIdSearchParameter";
import {
    type LensScoreDailyFieldsFragment,
    type LensScoreMetricsFieldsFragment,
    type LensScoreWindowFieldsFragment,
    type LensScoresQuery,
    type LensScoresQueryVariables,
    getLensScores,
} from "../api/queries";

import { useCursorPagedQuery } from "./useCursorPagedQuery";
import { useSelectedLens } from "./useSelectedLens";

export type LensPlusMonthlyRow = LensScoresQuery["lensScores"]["lensResults"][number];

interface LensPlusMonthlyScores {
    rows: LensPlusMonthlyRow[];
    window: (LensScoreWindowFieldsFragment & LensScoreDailyFieldsFragment) | null;
    estimatedTotals: LensScoreMetricsFieldsFragment | null;
    missingTotalsCount: number;
    paidAmountMilliUsd: number;
    submittedCount: number | null;
    scoredCount: number | null;
    availableScoredCount: number | null;
    payoutMinimumScore: number;
    hasMore: boolean;
    fetchMore: () => void;
    loadingMore: boolean;
    hasMoreError: boolean;
    lensId: string | null;
    lensName: string | undefined;
    clearLensId: () => void;
    loading: boolean;
    hasError: boolean;
    hasRefreshError: boolean;
    refetch: () => void;
}

const PAGE = {
    cursor: (data: LensScoresQuery) => data.lensScores.cursor,
    merge: (previous: LensScoresQuery, next: LensScoresQuery): LensScoresQuery => {
        const existing = new Set(previous.lensScores.lensResults.map((row) => row.unlockableId));
        return {
            ...next,
            lensScores: {
                ...next.lensScores,
                lensResults: [
                    ...previous.lensScores.lensResults,
                    ...next.lensScores.lensResults.filter((row) => !existing.has(row.unlockableId)),
                ],
            },
        };
    },
};

export const useLensPlusMonthlyScores = (
    month: string,
    skip = false,
    options: {
        summaryOnly?: boolean;
        ignoreSelection?: boolean;
        listOnly?: boolean;
        includeDailyBreakdown?: boolean;
    } = {},
): LensPlusMonthlyScores => {
    const {
        summaryOnly = false,
        ignoreSelection = false,
        listOnly = false,
        includeDailyBreakdown = !summaryOnly && !listOnly,
    } = options;
    const { lensId: selectedLensId, clearLensId } = useLensIdSearchParameter();
    const lensId = ignoreSelection ? null : selectedLensId;
    const { lens, unlockableId, query: selectedLens } = useSelectedLens(lensId, skip);
    const variables = useMemo<LensScoresQueryVariables>(
        () => ({
            input: {
                calendarMonth: `${month}-01T00:00:00.000Z`,
                unlockableIds: unlockableId ? [unlockableId] : [],
                includeCreatorTotal: !lensId && !listOnly,
                includeDailyBreakdown,
                limit: summaryOnly ? 1 : 200,
            },
            includeLensDaily: Boolean(lensId) && includeDailyBreakdown,
            includeLensMetadata: !summaryOnly,
        }),
        [month, lensId, unlockableId, summaryOnly, listOnly, includeDailyBreakdown],
    );
    const lensPending = Boolean(lensId && !unlockableId);
    const paged = useCursorPagedQuery<LensScoresQuery, LensScoresQueryVariables>(
        getLensScores,
        variables,
        skip || lensPending,
        PAGE,
    );
    const response = paged.response?.lensScores;
    const rows = useMemo(() => [...(response?.lensResults ?? [])], [response?.lensResults]);
    const selectedWindow = lensId
        ? rows.find((row) => String(row.unlockableId) === unlockableId)?.selectedMonth
        : response?.creatorTotal?.selectedMonth;
    const window = useMemo(
        () => (selectedWindow ? { ...selectedWindow, dailyBuckets: selectedWindow.dailyBuckets ?? [] } : null),
        [selectedWindow],
    );
    const estimatedTotals = !lensId && !listOnly && !window?.totals ? response?.summary.availableTotals ?? null : null;
    const missingTotalsCount = response?.summary.missingTotalsCount ?? 0;
    // A failed or unresolved Lens lookup is what blocks the page query, so retry that first.
    const retryLens = lensId && (Boolean(selectedLens.error) || !unlockableId) ? selectedLens.refetch : undefined;
    const refetch = useCallback(() => {
        if (!skip) paged.refetch(retryLens);
    }, [paged, retryLens, skip]);
    return {
        rows,
        window,
        estimatedTotals,
        missingTotalsCount,
        paidAmountMilliUsd: response?.summary.paidAmountMilliUsd ?? 0,
        submittedCount: response?.summary.submittedCount ?? null,
        scoredCount: response?.summary.scoredCount ?? null,
        availableScoredCount: response?.summary.availableScoredCount ?? null,
        payoutMinimumScore: response?.payoutMinimumScore ?? 100,
        hasMore: paged.hasMore,
        fetchMore: paged.fetchMore,
        loadingMore: paged.loadingMore,
        hasMoreError: paged.hasMoreError,
        lensId,
        lensName: lens?.name,
        clearLensId,
        loading: !skip && (Boolean(lensId && selectedLens.loading) || paged.loading),
        hasError:
            !skip &&
            (paged.hasError || Boolean(lensId && (selectedLens.error ?? (!selectedLens.loading && !unlockableId)))),
        hasRefreshError: paged.hasRefreshError,
        refetch,
    };
};
